/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import {
  createShuffledIndices,
  getNextTrackIndex,
  playlistTrackToAudioTrack,
  type AudioTrack,
  type PersistedQueueState,
  type RepeatMode,
  type Playlist,
  type QueuePosition,
} from '@/types/audio';
import { loadPersistedQueueState } from '@/hooks/useQueuePersistence';
import { useAuthCleanup } from './AuthCleanupContext';
import { useAuth } from './AuthContext';

// Re-export AudioTrack for backwards compatibility
export type { AudioTrack } from '@/types/audio';

// ==================== Types ====================

/** High-frequency playback values separated to avoid re-rendering all consumers */
interface AudioPlaybackContextValue {
  currentTime: number;
  duration: number;
  isLoading: boolean;
  seek: (time: number) => void;
}

/** Stable context values that change infrequently */
interface AudioStableContextValue {
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  volume: number;
  playbackRate: number;

  // Queue state
  queue: AudioTrack[];
  queueIndex: number;
  shuffleMode: boolean;
  repeatMode: RepeatMode;
  activePlaylistId: string | null;

  // Basic playback actions
  playAudio: (track: AudioTrack, sourcePosition?: DOMRect) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;

  // Queue actions
  addToQueue: (track: AudioTrack, position: QueuePosition, sourcePosition?: DOMRect) => void;
  removeFromQueue: (index: number) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  clearQueue: () => void;
  playFromQueue: (index: number) => void;
  playNext: () => void;
  playPrevious: () => void;

  // Playback mode actions
  setShuffleMode: (enabled: boolean) => void;
  setRepeatMode: (mode: RepeatMode) => void;
  toggleShuffle: () => void;
  cycleRepeatMode: () => void;

  // Playlist actions
  playPlaylist: (playlist: Playlist, startIndex?: number) => void;

  // Animation
  animationSource: DOMRect | null;
  animationTargetRef: React.RefObject<HTMLButtonElement | null>;
  clearAnimation: () => void;

  // Helpers
  hasNext: boolean;
  hasPrevious: boolean;
}

/** Combined type returned by useAudio() for backwards compatibility */
type AudioContextValue = AudioStableContextValue & AudioPlaybackContextValue;

interface AudioProviderProps {
  children: ReactNode;
}

// ==================== Context ====================

const AudioStableContext = createContext<AudioStableContextValue | null>(null);
const AudioPlaybackContext = createContext<AudioPlaybackContextValue | null>(null);

// ==================== Constants ====================

const LEGACY_QUEUE_SAVE_KEY = 'spirit-of-shabbat-audio-queue';
const SAVE_DEBOUNCE_MS = 1000;

/** Generate a user-scoped localStorage key for audio queue persistence */
function getQueueSaveKey(userId: string): string {
  return `spirit-of-shabbat-audio-queue-${userId}`;
}

interface ClearPlayerStateParams {
  setCurrentTrack: (t: AudioTrack | null) => void;
  setIsPlaying: (p: boolean) => void;
  setCurrentTime: (t: number) => void;
  setDuration: (d: number) => void;
  setQueueWithRef: (updater: AudioTrack[] | ((prev: AudioTrack[]) => AudioTrack[])) => void;
  setQueueIndexWithRef: (updater: number | ((prev: number) => number)) => void;
  setActivePlaylistId: (id: string | null) => void;
  setShuffledIndices: (indices: number[]) => void;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  hasLoadedPersistedTrackRef: React.MutableRefObject<boolean>;
}

function clearPlayerStateOnUserSwitch(params: ClearPlayerStateParams): void {
  const {
    setCurrentTrack,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setQueueWithRef,
    setQueueIndexWithRef,
    setActivePlaylistId,
    setShuffledIndices,
    audioRef,
    hasLoadedPersistedTrackRef,
  } = params;

  setCurrentTrack(null);
  setIsPlaying(false);
  setCurrentTime(0);
  setDuration(0);
  setQueueWithRef([]);
  setQueueIndexWithRef(-1);
  setActivePlaylistId(null);
  setShuffledIndices([]);
  hasLoadedPersistedTrackRef.current = false;

  const audio = audioRef.current;
  if (audio) {
    audio.pause();
    audio.src = '';
  }
}

interface RestorePersistedStateParams {
  persisted: PersistedQueueState;
  setQueueWithRef: (updater: AudioTrack[] | ((prev: AudioTrack[]) => AudioTrack[])) => void;
  setQueueIndexWithRef: (updater: number | ((prev: number) => number)) => void;
  setVolumeState: (v: number) => void;
  setPlaybackRateState: (r: number) => void;
  setShuffleModeState: (v: boolean) => void;
  setRepeatModeState: (m: RepeatMode) => void;
  setActivePlaylistId: (id: string | null) => void;
  setShuffledIndices: (indices: number[]) => void;
  setCurrentTrack: (t: AudioTrack | null) => void;
  pendingSeekTimeRef: React.MutableRefObject<number | null>;
  hasLoadedPersistedTrackRef: React.MutableRefObject<boolean>;
}

function restorePersistedState(params: RestorePersistedStateParams): void {
  const {
    persisted,
    setQueueWithRef,
    setQueueIndexWithRef,
    setVolumeState,
    setPlaybackRateState,
    setShuffleModeState,
    setRepeatModeState,
    setActivePlaylistId,
    setShuffledIndices,
    setCurrentTrack,
    pendingSeekTimeRef,
    hasLoadedPersistedTrackRef,
  } = params;

  setQueueWithRef(persisted.queue);
  setQueueIndexWithRef(persisted.currentIndex);
  setVolumeState(persisted.volume);
  setPlaybackRateState(persisted.playbackRate);
  setShuffleModeState(persisted.shuffleMode);
  setRepeatModeState(persisted.repeatMode);
  if (persisted.activePlaylistId) {
    setActivePlaylistId(persisted.activePlaylistId);
  }
  if (persisted.shuffledIndices) {
    setShuffledIndices(persisted.shuffledIndices);
  }

  if (persisted.currentIndex >= 0 && persisted.currentIndex < persisted.queue.length) {
    const track = persisted.queue[persisted.currentIndex];
    setCurrentTrack(track);
    if (persisted.currentTime > 0) {
      pendingSeekTimeRef.current = persisted.currentTime;
    }
  } else if (persisted.queue.length > 0) {
    setQueueIndexWithRef(0);
    setCurrentTrack(persisted.queue[0]);
  }

  hasLoadedPersistedTrackRef.current = false;
}

// ==================== Provider ====================

export function AudioProvider({ children }: AudioProviderProps) {
  const { registerCleanup } = useAuthCleanup();
  const { authState } = useAuth();

  // Derive the current user ID (null when not authenticated)
  const currentUserId = authState.status === 'authenticated' ? authState.user.uid : null;
  const currentUserIdRef = useRef<string | null>(null);

  // Current playback state
  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Queue state
  const [queue, setQueue] = useState<AudioTrack[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [shuffleMode, setShuffleModeState] = useState(false);
  const [repeatMode, setRepeatModeState] = useState<RepeatMode>('off');
  const [activePlaylistId, setActivePlaylistId] = useState<string | null>(null);
  const [shuffledIndices, setShuffledIndices] = useState<number[]>([]);

  // Refs to track current state (avoids stale closures in callbacks)
  const queueRef = useRef<AudioTrack[]>([]);
  const queueIndexRef = useRef(-1);
  const shuffledIndicesRef = useRef<number[]>([]);
  const shuffleModeRef = useRef(false);
  const repeatModeRef = useRef<RepeatMode>('off');

  // Wrappers that update both state and ref synchronously, preventing stale reads
  // when multiple calls happen in the same event loop tick.
  const setQueueWithRef = useCallback(
    (updater: AudioTrack[] | ((prev: AudioTrack[]) => AudioTrack[])) => {
      setQueue((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        queueRef.current = next;
        return next;
      });
    },
    []
  );

  const setQueueIndexWithRef = useCallback((updater: number | ((prev: number) => number)) => {
    setQueueIndex((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      queueIndexRef.current = next;
      return next;
    });
  }, []);

  // Keep remaining refs in sync with state
  useEffect(() => {
    shuffledIndicesRef.current = shuffledIndices;
  }, [shuffledIndices]);

  useEffect(() => {
    shuffleModeRef.current = shuffleMode;
  }, [shuffleMode]);

  useEffect(() => {
    repeatModeRef.current = repeatMode;
  }, [repeatMode]);

  // Keep currentTimeRef in sync for use in callbacks (fixes stale closure in playPrevious)
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // Animation state
  const [animationSource, setAnimationSource] = useState<DOMRect | null>(null);

  // Audio element ready state (triggers effect re-evaluation)
  const [audioElementReady, setAudioElementReady] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationTargetRef = useRef<HTMLButtonElement | null>(null);
  const pendingPlayRef = useRef<boolean>(false);
  const saveTimeoutRef = useRef<number | null>(null);
  const pendingSeekTimeRef = useRef<number | null>(null);
  const playNextTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedPersistedTrackRef = useRef(false);

  // Play request tracking - prevents race conditions from stale play requests
  // Each new play request gets a unique ID, and only the latest ID is honored
  const playRequestIdRef = useRef(0);

  // Ref to track currentTime for use in callbacks (avoids stale closures)
  const currentTimeRef = useRef(0);

  // Refs for values used in the beforeunload handler (avoids dependency churn)
  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  const playbackRateRef = useRef(playbackRate);
  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);
  const activePlaylistIdRef = useRef(activePlaylistId);
  useEffect(() => {
    activePlaylistIdRef.current = activePlaylistId;
  }, [activePlaylistId]);

  // Register cleanup function for auth logout
  useEffect(() => {
    registerCleanup('audio-context', () => {
      // Stop audio playback
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.currentTime = 0;
      }

      // Clear timers
      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (playNextTimeoutRef.current) {
        clearTimeout(playNextTimeoutRef.current);
        playNextTimeoutRef.current = null;
      }

      // Reset state
      setCurrentTrack(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setVolumeState(1);
      setPlaybackRateState(1);
      setIsLoading(false);
      setQueueWithRef([]);
      setQueueIndexWithRef(-1);
      setShuffleModeState(false);
      setRepeatModeState('off');
      setActivePlaylistId(null);
      setShuffledIndices([]);
      setAnimationSource(null);
      pendingPlayRef.current = false;
      pendingSeekTimeRef.current = null;
      hasLoadedPersistedTrackRef.current = false;

      // Clear persisted state for the current user
      const userId = currentUserIdRef.current;
      try {
        if (userId) {
          localStorage.removeItem(getQueueSaveKey(userId));
        }
        // Also clean up legacy non-user-scoped key
        localStorage.removeItem(LEGACY_QUEUE_SAVE_KEY);
      } catch (e) {
        console.error('Error clearing queue from storage:', e);
      }

      // Reset user tracking so re-login triggers fresh state load
      currentUserIdRef.current = null;
    });
  }, [registerCleanup, setQueueIndexWithRef, setQueueWithRef]);

  // ==================== Persistence ====================

  // Save queue state to localStorage (debounced)
  // Only saves when there is an authenticated user (user-scoped key)
  const saveQueueState = useCallback(() => {
    const userId = currentUserIdRef.current;
    if (!userId) return; // Don't persist if no user is authenticated

    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      // Re-check userId inside the timeout in case user logged out during debounce
      const savedUserId = currentUserIdRef.current;
      if (!savedUserId) return;

      try {
        const state = {
          queue,
          currentIndex: queueIndex,
          currentTime: audioRef.current?.currentTime || 0,
          volume,
          playbackRate,
          shuffleMode,
          repeatMode,
          activePlaylistId,
          shuffledIndices,
          timestamp: Date.now(),
        };
        localStorage.setItem(getQueueSaveKey(savedUserId), JSON.stringify(state));
      } catch (error) {
        console.error('Error saving queue state:', error);
      }
      saveTimeoutRef.current = null;
    }, SAVE_DEBOUNCE_MS);
  }, [
    queue,
    queueIndex,
    volume,
    playbackRate,
    shuffleMode,
    repeatMode,
    activePlaylistId,
    shuffledIndices,
  ]);

  // Ref to access latest saveQueueState in event handlers (fixes stale closure)
  const saveQueueStateRef = useRef(saveQueueState);
  useEffect(() => {
    saveQueueStateRef.current = saveQueueState;
  }, [saveQueueState]);

  // Cleanup timeout refs on unmount
  useEffect(() => {
    return () => {
      if (playNextTimeoutRef.current) {
        clearTimeout(playNextTimeoutRef.current);
      }
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentUserId === currentUserIdRef.current) return;

    const previousUserId = currentUserIdRef.current;
    currentUserIdRef.current = currentUserId;

    if (!currentUserId) return;

    if (previousUserId && previousUserId !== currentUserId) {
      clearPlayerStateOnUserSwitch({
        setCurrentTrack,
        setIsPlaying,
        setCurrentTime,
        setDuration,
        setQueueWithRef,
        setQueueIndexWithRef,
        setActivePlaylistId,
        setShuffledIndices,
        audioRef,
        hasLoadedPersistedTrackRef,
      });
    }

    const persisted = loadPersistedQueueState(getQueueSaveKey(currentUserId));
    if (persisted) {
      restorePersistedState({
        persisted,
        setQueueWithRef,
        setQueueIndexWithRef,
        setVolumeState,
        setPlaybackRateState,
        setShuffleModeState,
        setRepeatModeState,
        setActivePlaylistId,
        setShuffledIndices,
        setCurrentTrack,
        pendingSeekTimeRef,
        hasLoadedPersistedTrackRef,
      });
    }

    try {
      localStorage.removeItem(LEGACY_QUEUE_SAVE_KEY);
    } catch {
      // Ignore errors
    }
  }, [currentUserId, setQueueIndexWithRef, setQueueWithRef]);

  // Save on queue changes (only when authenticated and queue is non-empty)
  useEffect(() => {
    if (currentUserIdRef.current && queue.length > 0) {
      saveQueueState();
    }
  }, [queue, queueIndex, shuffleMode, repeatMode, saveQueueState]);

  // Periodic save during playback (every 5 seconds)
  useEffect(() => {
    if (!isPlaying) return;

    const intervalId = setInterval(() => {
      saveQueueState();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isPlaying, saveQueueState]);

  // Save on page unload (only for authenticated users).
  // Uses refs for all state values so the handler is registered only once.
  useEffect(() => {
    const handleBeforeUnload = () => {
      const userId = currentUserIdRef.current;
      if (!userId || queueRef.current.length === 0) return;

      try {
        const state = {
          queue: queueRef.current,
          currentIndex: queueIndexRef.current,
          currentTime: audioRef.current?.currentTime || 0,
          volume: volumeRef.current,
          playbackRate: playbackRateRef.current,
          shuffleMode: shuffleModeRef.current,
          repeatMode: repeatModeRef.current,
          activePlaylistId: activePlaylistIdRef.current,
          shuffledIndices: shuffledIndicesRef.current,
          timestamp: Date.now(),
        };
        localStorage.setItem(getQueueSaveKey(userId), JSON.stringify(state));
      } catch (error) {
        console.error('Error saving queue state on unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ==================== Audio Element Setup ====================

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
    audioRef.current.playbackRate = playbackRate;
    audioRef.current.preload = 'auto';

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      pendingPlayRef.current = false;

      // Auto-advance based on repeat mode
      handleTrackEnded();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      pendingPlayRef.current = false;
    };

    const handlePause = () => {
      setIsPlaying(false);
      // Save state on pause to capture current position
      // Use ref to avoid stale closure (saveQueueState changes frequently)
      saveQueueStateRef.current();
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);

      // If there's a pending seek time from restored state, apply it
      if (pendingSeekTimeRef.current !== null) {
        const seekTime = pendingSeekTimeRef.current;
        pendingSeekTimeRef.current = null;
        audio.currentTime = Math.min(seekTime, audio.duration || seekTime);
        setCurrentTime(audio.currentTime);
      }

      // Only attempt play if there's a pending play request
      // The pendingPlayRef is set BEFORE audio.load() to fix the race condition
      // where canplay could fire before the flag was set
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false;

        // Guard: Only play if audio element is still valid and not paused by user
        // This prevents stale play requests from triggering after pause/stop
        if (audio.src && audio.readyState >= 3) {
          audio.play().catch((err) => {
            // AbortError is expected when rapidly switching tracks
            if (err.name !== 'AbortError') {
              console.error('Audio playback failed:', err);
            }
            setIsPlaying(false);
            setIsLoading(false);
          });
        }
      }
    };

    const handleError = (e: Event) => {
      setIsLoading(false);
      setIsPlaying(false);
      pendingPlayRef.current = false;
      const target = e.target as HTMLAudioElement;
      console.error('Audio playback error:', target.error?.message || 'Unknown error');
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    setAudioElementReady(true);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!audioElementReady || !audioRef.current) return;
    if (!currentTrack || hasLoadedPersistedTrackRef.current) return;

    // Only load if the audio element doesn't have a source yet
    if (!audioRef.current.src && currentTrack.url) {
      hasLoadedPersistedTrackRef.current = true;
      audioRef.current.src = currentTrack.url;
      audioRef.current.load();
    }
  }, [currentTrack, audioElementReady]);

  // ==================== Internal Play Function ====================

  const playFromQueueInternal = useCallback(
    (index: number) => {
      const currentQueue = queueRef.current;
      if (index < 0 || index >= currentQueue.length) return;

      const track = currentQueue[index];
      const audio = audioRef.current;
      if (!audio || !track.url) return;

      // Increment play request ID to track this request
      // This allows us to ignore stale canplay events from previous requests
      const requestId = ++playRequestIdRef.current;

      // Stop any current playback first
      audio.pause();

      // Set pending play BEFORE load to fix race condition
      // The canplay event may fire very quickly, so we need the flag set first
      pendingPlayRef.current = true;

      setQueueIndexWithRef(index);
      setCurrentTrack(track);
      setCurrentTime(0);
      setDuration(0);
      setIsLoading(true);

      audio.src = track.url;
      audio.load();

      // Verify this is still the current request after load initiated
      // (load is synchronous but canplay is async)
      if (playRequestIdRef.current !== requestId) {
        pendingPlayRef.current = false;
      }
    },
    [setQueueIndexWithRef]
  ); // Uses queueRef to avoid stale closures

  // ==================== Track End Handler ====================

  const handleTrackEnded = useCallback(() => {
    const currentQueue = queueRef.current;
    const currentQueueIndex = queueIndexRef.current;
    const currentRepeatMode = repeatModeRef.current;

    if (currentRepeatMode === 'one') {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Repeat playback failed:', err);
          }
        });
      }
      return;
    }

    const result = getNextTrackIndex({
      shuffleMode: shuffleModeRef.current,
      repeatMode: currentRepeatMode,
      currentIndex: currentQueueIndex,
      shuffledIndices: shuffledIndicesRef.current,
      queueLength: currentQueue.length,
    });

    if (!result) {
      setCurrentTime(0);
      return;
    }

    if (result.newShuffledIndices) {
      setShuffledIndices(result.newShuffledIndices);
    }

    if (result.nextIndex >= 0 && result.nextIndex < currentQueue.length) {
      playFromQueueInternal(result.nextIndex);
    }
  }, [playFromQueueInternal]);

  // ==================== Basic Playback Actions ====================

  const playAudio = useCallback(
    (track: AudioTrack, sourcePosition?: DOMRect) => {
      const audio = audioRef.current;
      if (!audio) return;

      if (!track.url || typeof track.url !== 'string' || track.url.trim() === '') {
        console.error('Invalid audio URL:', track.url);
        return;
      }

      // If same track, toggle play/pause
      if (currentTrack?.id === track.id && currentTrack?.url === track.url) {
        if (isPlaying) {
          audio.pause();
        } else {
          audio.play().catch(console.error);
        }
        return;
      }

      if (sourcePosition) {
        setAnimationSource(sourcePosition);
      }

      // Increment play request ID to track this request
      ++playRequestIdRef.current;

      // Add to queue and play (replaces queue)
      setQueueWithRef([track]);
      setQueueIndexWithRef(0);
      setActivePlaylistId(null);
      setShuffledIndices([]);

      // Stop any current playback
      audio.pause();

      // Set pending play BEFORE load to fix race condition
      pendingPlayRef.current = true;

      setCurrentTrack(track);
      setCurrentTime(0);
      setDuration(0);
      setIsLoading(true);

      audio.src = track.url;
      audio.load();
    },
    [currentTrack, isPlaying, setQueueIndexWithRef, setQueueWithRef]
  );

  const pause = useCallback(() => {
    pendingPlayRef.current = false;
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.src) {
      audio.play().catch((err) => {
        console.error('Resume playback failed:', err);
        setIsPlaying(false);
      });
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    pendingPlayRef.current = false;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
    }
    setCurrentTrack(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(time, audio.duration || 0));
    }
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackRateState(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, []);

  // ==================== Queue Actions ====================

  const addToQueue = useCallback(
    (track: AudioTrack, position: QueuePosition, sourcePosition?: DOMRect) => {
      // Validate track before processing
      if (!track || !track.url || typeof track.url !== 'string' || track.url.trim() === '') {
        console.error('Invalid track in addToQueue:', track);
        return;
      }

      if (sourcePosition) {
        setAnimationSource(sourcePosition);
      }

      const audio = audioRef.current;
      // Use refs to get current values (avoids stale closures)
      const currentQueue = queueRef.current;
      const currentQueueIndex = queueIndexRef.current;
      const wasEmpty = currentQueue.length === 0;

      if (position === 'now') {
        const insertIndex = currentQueueIndex + 1;
        setQueueWithRef((prev) => {
          const newQueue = [...prev];
          newQueue.splice(insertIndex, 0, track);
          return newQueue;
        });
        setQueueIndexWithRef(insertIndex);

        if (audio) {
          ++playRequestIdRef.current;
          audio.pause();

          setCurrentTrack(track);
          setCurrentTime(0);
          setDuration(0);
          setIsLoading(true);

          audio.src = track.url;
          // Call play() directly so it stays within the user gesture call stack
          // (required by Safari's autoplay policy). The browser handles loading
          // automatically; pendingPlayRef is kept as a fallback for canplay.
          pendingPlayRef.current = true;
          audio.play().catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('Audio playback failed:', err);
            }
          });
        }
      } else if (position === 'next') {
        const insertIndex = Math.max(0, currentQueueIndex + 1);
        setQueueWithRef((prev) => {
          const newQueue = [...prev];
          newQueue.splice(insertIndex, 0, track);
          return newQueue;
        });

        if (wasEmpty && audio) {
          setQueueIndexWithRef(0);
          setCurrentTrack(track);
          setCurrentTime(0);
          setDuration(0);
          setIsLoading(true);
          audio.src = track.url;
          pendingPlayRef.current = true;
          audio.play().catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('Audio playback failed:', err);
            }
          });
        }
      } else {
        setQueueWithRef((prev) => [...prev, track]);

        if (wasEmpty && audio) {
          setQueueIndexWithRef(0);
          setCurrentTrack(track);
          setCurrentTime(0);
          setDuration(0);
          setIsLoading(true);
          audio.src = track.url;
          pendingPlayRef.current = true;
          audio.play().catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('Audio playback failed:', err);
            }
          });
        }
      }

      setActivePlaylistId(null);
    },
    [setQueueIndexWithRef, setQueueWithRef]
  ); // No dependencies needed - uses refs for current values

  const removeFromQueue = useCallback(
    (index: number) => {
      const currentQueue = queueRef.current;
      const currentQueueIndex = queueIndexRef.current;

      if (index < 0 || index >= currentQueue.length) return;

      setQueueWithRef((prev) => {
        const newQueue = [...prev];
        newQueue.splice(index, 1);
        return newQueue;
      });

      // Adjust current index if needed
      if (index < currentQueueIndex) {
        setQueueIndexWithRef((prev) => prev - 1);
      } else if (index === currentQueueIndex) {
        // Removed current track
        if (currentQueue.length > 1) {
          // Play next track (or previous if at end)
          const nextIndex = index < currentQueue.length - 1 ? index : index - 1;
          // Clear any existing timeout before setting a new one
          if (playNextTimeoutRef.current) {
            clearTimeout(playNextTimeoutRef.current);
          }
          playNextTimeoutRef.current = setTimeout(() => playFromQueueInternal(nextIndex), 0);
        } else {
          // Queue is now empty
          stop();
        }
      }
    },
    [playFromQueueInternal, stop, setQueueIndexWithRef, setQueueWithRef]
  ); // Uses refs for queue state

  const reorderQueue = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentQueue = queueRef.current;
      const currentQueueIndex = queueIndexRef.current;

      if (
        fromIndex < 0 ||
        fromIndex >= currentQueue.length ||
        toIndex < 0 ||
        toIndex >= currentQueue.length ||
        fromIndex === toIndex
      )
        return;

      setQueueWithRef((prev) => {
        const newQueue = [...prev];
        const [moved] = newQueue.splice(fromIndex, 1);
        newQueue.splice(toIndex, 0, moved);
        return newQueue;
      });

      // Adjust current index
      if (fromIndex === currentQueueIndex) {
        setQueueIndexWithRef(toIndex);
      } else if (fromIndex < currentQueueIndex && toIndex >= currentQueueIndex) {
        setQueueIndexWithRef((prev) => prev - 1);
      } else if (fromIndex > currentQueueIndex && toIndex <= currentQueueIndex) {
        setQueueIndexWithRef((prev) => prev + 1);
      }
    },
    [setQueueIndexWithRef, setQueueWithRef]
  ); // Uses refs for queue state

  const clearQueue = useCallback(() => {
    stop();
    setQueueWithRef([]);
    setQueueIndexWithRef(-1);
    setActivePlaylistId(null);
    setShuffledIndices([]);

    // Clear persisted state for the current user
    const userId = currentUserIdRef.current;
    try {
      if (userId) {
        localStorage.removeItem(getQueueSaveKey(userId));
      }
      // Also remove legacy key if it exists
      localStorage.removeItem(LEGACY_QUEUE_SAVE_KEY);
    } catch (e) {
      console.error('Error clearing queue from storage:', e);
    }
  }, [stop, setQueueIndexWithRef, setQueueWithRef]);

  const playFromQueue = useCallback(
    (index: number) => {
      playFromQueueInternal(index);
    },
    [playFromQueueInternal]
  );

  const playNext = useCallback(() => {
    const currentQueue = queueRef.current;
    const currentQueueIndex = queueIndexRef.current;
    const currentShuffleMode = shuffleModeRef.current;
    const currentShuffledIndices = shuffledIndicesRef.current;
    const currentRepeatMode = repeatModeRef.current;

    if (currentQueue.length === 0) return;

    let nextIndex: number;
    if (currentShuffleMode && currentShuffledIndices.length > 0) {
      const currentShufflePos = currentShuffledIndices.indexOf(currentQueueIndex);
      const nextShufflePos = currentShufflePos + 1;
      if (nextShufflePos < currentShuffledIndices.length) {
        nextIndex = currentShuffledIndices[nextShufflePos];
      } else if (currentRepeatMode === 'all') {
        const newShuffled = createShuffledIndices(currentQueue.length, 0);
        setShuffledIndices(newShuffled);
        nextIndex = newShuffled[0];
      } else {
        return; // End of queue
      }
    } else {
      nextIndex = currentQueueIndex + 1;
      if (nextIndex >= currentQueue.length) {
        if (currentRepeatMode === 'all') {
          nextIndex = 0;
        } else {
          return; // End of queue
        }
      }
    }

    playFromQueueInternal(nextIndex);
  }, [playFromQueueInternal]); // Uses refs for state

  const playPrevious = useCallback(() => {
    const currentQueue = queueRef.current;
    const currentQueueIndex = queueIndexRef.current;
    const currentShuffleMode = shuffleModeRef.current;
    const currentShuffledIndices = shuffledIndicesRef.current;
    const currentRepeatMode = repeatModeRef.current;

    if (currentQueue.length === 0) return;

    // If more than 3 seconds in, restart current track
    // Use ref instead of state to avoid stale closure issues
    if (currentTimeRef.current > 3) {
      seek(0);
      return;
    }

    let prevIndex: number;
    if (currentShuffleMode && currentShuffledIndices.length > 0) {
      const currentShufflePos = currentShuffledIndices.indexOf(currentQueueIndex);
      const prevShufflePos = currentShufflePos - 1;
      if (prevShufflePos >= 0) {
        prevIndex = currentShuffledIndices[prevShufflePos];
      } else if (currentRepeatMode === 'all') {
        prevIndex = currentShuffledIndices[currentShuffledIndices.length - 1];
      } else {
        seek(0); // Start of queue, restart track
        return;
      }
    } else {
      prevIndex = currentQueueIndex - 1;
      if (prevIndex < 0) {
        if (currentRepeatMode === 'all') {
          prevIndex = currentQueue.length - 1;
        } else {
          seek(0); // Start of queue, restart track
          return;
        }
      }
    }

    playFromQueueInternal(prevIndex);
  }, [seek, playFromQueueInternal]); // Removed currentTime - uses ref now

  // ==================== Playback Mode Actions ====================

  const setShuffleMode = useCallback(
    (enabled: boolean) => {
      setShuffleModeState(enabled);
      // Use refs to avoid stale closures
      const currentQueue = queueRef.current;
      const currentQueueIndex = queueIndexRef.current;
      if (enabled && currentQueue.length > 1) {
        // Create shuffled indices starting from current position
        const indices = createShuffledIndices(currentQueue.length, currentQueueIndex);
        setShuffledIndices(indices);
      } else {
        setShuffledIndices([]);
      }
    },
    [] // No dependencies needed - uses refs for current values
  );

  const toggleShuffle = useCallback(() => {
    setShuffleMode(!shuffleMode);
  }, [shuffleMode, setShuffleMode]);

  const setRepeatMode = useCallback((mode: RepeatMode) => {
    setRepeatModeState(mode);
  }, []);

  const cycleRepeatMode = useCallback(() => {
    setRepeatModeState((prev) => {
      switch (prev) {
        case 'off':
          return 'all';
        case 'all':
          return 'one';
        case 'one':
          return 'off';
      }
    });
  }, []);

  // ==================== Playlist Actions ====================

  const playPlaylist = useCallback(
    (playlist: Playlist, startIndex = 0) => {
      if (playlist.tracks.length === 0) return;

      const audioTracks = playlist.tracks.map(playlistTrackToAudioTrack);
      setQueueWithRef(audioTracks);
      setActivePlaylistId(playlist.id);

      if (shuffleMode) {
        const indices = createShuffledIndices(audioTracks.length, startIndex);
        setShuffledIndices(indices);
        playFromQueueInternal(indices[0]);
      } else {
        setShuffledIndices([]);
        playFromQueueInternal(startIndex);
      }
    },
    [shuffleMode, playFromQueueInternal, setQueueWithRef]
  );

  // ==================== Animation ====================

  const clearAnimation = useCallback(() => {
    setAnimationSource(null);
  }, []);

  // ==================== Computed Values ====================

  const hasNext = useMemo(() => {
    if (queue.length === 0) return false;
    if (repeatMode === 'all') return true;
    if (shuffleMode && shuffledIndices.length > 0) {
      const pos = shuffledIndices.indexOf(queueIndex);
      return pos < shuffledIndices.length - 1;
    }
    return queueIndex < queue.length - 1;
  }, [queue.length, queueIndex, repeatMode, shuffleMode, shuffledIndices]);

  const hasPrevious = useMemo(() => {
    if (queue.length === 0) return false;
    if (currentTime > 3) return true; // Can restart current track
    if (repeatMode === 'all') return true;
    if (shuffleMode && shuffledIndices.length > 0) {
      const pos = shuffledIndices.indexOf(queueIndex);
      return pos > 0;
    }
    return queueIndex > 0;
  }, [queue.length, queueIndex, currentTime, repeatMode, shuffleMode, shuffledIndices]);

  // ==================== Context Values ====================

  // High-frequency playback values (currentTime updates many times per second)
  const playbackValue: AudioPlaybackContextValue = useMemo(
    () => ({ currentTime, duration, isLoading, seek }),
    [currentTime, duration, isLoading, seek]
  );

  // Stable values that change infrequently (no currentTime/duration/isLoading)
  const stableValue: AudioStableContextValue = useMemo(
    () => ({
      currentTrack,
      isPlaying,
      volume,
      playbackRate,
      queue,
      queueIndex,
      shuffleMode,
      repeatMode,
      activePlaylistId,
      playAudio,
      pause,
      resume,
      stop,
      setVolume,
      setPlaybackRate,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      clearQueue,
      playFromQueue,
      playNext,
      playPrevious,
      setShuffleMode,
      setRepeatMode,
      toggleShuffle,
      cycleRepeatMode,
      playPlaylist,
      animationSource,
      animationTargetRef,
      clearAnimation,
      hasNext,
      hasPrevious,
    }),
    [
      currentTrack,
      isPlaying,
      volume,
      playbackRate,
      queue,
      queueIndex,
      shuffleMode,
      repeatMode,
      activePlaylistId,
      playAudio,
      pause,
      resume,
      stop,
      setVolume,
      setPlaybackRate,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      clearQueue,
      playFromQueue,
      playNext,
      playPrevious,
      setShuffleMode,
      setRepeatMode,
      toggleShuffle,
      cycleRepeatMode,
      playPlaylist,
      animationSource,
      clearAnimation,
      hasNext,
      hasPrevious,
    ]
  );

  return (
    <AudioStableContext.Provider value={stableValue}>
      <AudioPlaybackContext.Provider value={playbackValue}>
        {children}
      </AudioPlaybackContext.Provider>
    </AudioStableContext.Provider>
  );
}

// ==================== Hooks ====================

/**
 * Full audio context (merges stable + playback). Most consumers should prefer
 * more specific hooks to avoid unnecessary re-renders from currentTime updates.
 */
export function useAudio(): AudioContextValue {
  const stable = useContext(AudioStableContext);
  const playback = useContext(AudioPlaybackContext);
  if (!stable || !playback) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return useMemo(() => ({ ...stable, ...playback }), [stable, playback]);
}

/**
 * Hook for high-frequency playback values (currentTime, duration, isLoading, seek).
 * Only components that display a progress bar or time should use this.
 */
export function useAudioPlayback(): AudioPlaybackContextValue {
  const context = useContext(AudioPlaybackContext);
  if (!context) {
    throw new Error('useAudioPlayback must be used within an AudioProvider');
  }
  return context;
}

/**
 * Hook to check if audio is currently playing
 */
export function useIsAudioPlaying(): boolean {
  const stable = useContext(AudioStableContext);
  if (!stable) throw new Error('useIsAudioPlaying must be used within an AudioProvider');
  return stable.isPlaying;
}

/**
 * Hook to get current track info
 */
export function useCurrentTrack(): AudioTrack | null {
  const stable = useContext(AudioStableContext);
  if (!stable) throw new Error('useCurrentTrack must be used within an AudioProvider');
  return stable.currentTrack;
}

/**
 * Hook to check if a specific track is currently playing
 */
export function useIsTrackPlaying(trackId: string): boolean {
  const stable = useContext(AudioStableContext);
  if (!stable) throw new Error('useIsTrackPlaying must be used within an AudioProvider');
  return stable.currentTrack?.id === trackId && stable.isPlaying;
}

/**
 * Hook to get queue state
 */
export function useQueue() {
  const stable = useContext(AudioStableContext);
  if (!stable) throw new Error('useQueue must be used within an AudioProvider');
  return {
    queue: stable.queue,
    queueIndex: stable.queueIndex,
    shuffleMode: stable.shuffleMode,
    repeatMode: stable.repeatMode,
    activePlaylistId: stable.activePlaylistId,
  };
}

/**
 * Hook to get queue actions
 */
export function useQueueActions() {
  const stable = useContext(AudioStableContext);
  if (!stable) throw new Error('useQueueActions must be used within an AudioProvider');
  return {
    addToQueue: stable.addToQueue,
    removeFromQueue: stable.removeFromQueue,
    reorderQueue: stable.reorderQueue,
    clearQueue: stable.clearQueue,
    playFromQueue: stable.playFromQueue,
    playNext: stable.playNext,
    playPrevious: stable.playPrevious,
    setShuffleMode: stable.setShuffleMode,
    setRepeatMode: stable.setRepeatMode,
    toggleShuffle: stable.toggleShuffle,
    cycleRepeatMode: stable.cycleRepeatMode,
    playPlaylist: stable.playPlaylist,
  };
}
